import { EmptyState } from '@/components/custom/empty-state';
import { Filter, FilterActions, FilterContent, FilterRadioGroup, FilterSelectNative } from '@/components/custom/filter';
import { useFormik } from 'formik';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { useWeeklyChallenge } from '@/hooks/useWeeklyChallenge';
import { DuelsNew } from '../duels-new';
import { useClient } from 'jsonapi-react';
import toast from 'react-hot-toast';
import { DuelsPlayer } from '../duels-player';
import { Loader } from '@/components/custom/loader';
import { Dialog, DialogTrigger } from '@/components/ui/dialog';
import * as Yup from 'yup';
import { DashboardContent } from '@/layouts/dashboard';
import { CardWrapper } from '@/components/ui/card';
import { PageContent, Page, PageSidebar } from '@/components/custom/page';
import { TiaBanner } from '@/components/custom/tia-banner';
import { DuelsPlayfulCard } from './DuelsPlayfulCard';
import { DuelsPlayfulRequests } from './DuelsPlayfulRequests';
import tiaDuelos from '@/assets/images/tia/TIA-COMEMORANDO.png';

export default function DuelsPlayful(props) {
  const { uri } = props;
  const [newDuelModal, setNewDuelModal] = useState(false);
  const [openPlayerModal, setOpenPlayerModal] = useState(false);
  const [duel, setDuel] = useState(false);
  const [taxonomies, setTaxonomies] = useState([]);
  const [duels, setDuels] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [loading, setLoading] = useState();
  const [loadingReceivedRequests, setLoadingReceivedRequests] = useState();
  const [loadingSentRequests, setLoadingSentRequests] = useState();
  const [winner, setWinner] = useState();
  const [activeReceivedTab, setActiveReceivedTab] = useState('active');
  const [selectedOpponent, setSelectedOpponent] = useState({});

  const client = useClient();

  const { setCurrentChallenge, setCurrentStep, setCurrentRound, setRightAnswers, setChallengeType } = useWeeklyChallenge();

  const filters = useFormik({
    initialValues: {
      topic: 'blank',
      status: 'ongoing'
    }
  });

  const getTaxonomies = async () => {
    try {
      const data = await client.fetch('/duels/taxonomies_for_creation');
      setTaxonomies(data);
    } catch (e) {
      console.error(e);
      toast.error(t('toast.errorGetTaxonomy'));
    }
  };

  const getSentRequests = async () => {
    setLoadingSentRequests(true);
    const { data, error } = await client.fetch('/duels/sent');
    if (error) {
      toast.error(t('toast.errorGetDuelsSentRequests'));
    } else {
      setSentRequests(data);
    }
    setLoadingSentRequests(false);
  };

  const getReceivedRequests = async () => {
    setLoadingReceivedRequests(true);
    const { data, error } = await client.fetch(`/duels/received?status=${activeReceivedTab}`);
    if (error) {
      toast.error(t('toast.errorGetDuelsReceivedRequests'));
    } else {
      setReceivedRequests(data);
    }
    setLoadingReceivedRequests(false);
  };

  const getDuels = async () => {
    let url = `/duels?status=${filters.values.status}`;
    if (filters.values.topic !== 'blank') {
      url;
    }

    const { data, error } = await client.fetch(url);
    if (error) {
      toast.error(t('toast.errorGetDuels'));
    } else {
      setDuels(data);
    }
  };

  useEffect(() => {
    getReceivedRequests();
  }, [activeReceivedTab]);

  const handleStart = async duel => {
    setCurrentStep(1);
    setDuel(duel);
    setChallengeType('duel');
    setCurrentChallenge(duel);
    setCurrentRound(duel?.['rounds-completed-by-me'] + 1);
    setOpenPlayerModal(true);
    setRightAnswers(duel?.['rounds-won-by-me']);
  };

  const getWinner = async id => {
    const { data, error } = await client.fetch(`/duels/${id}/winner`);

    if (error) {
      toast.error('Erro ao buscar vencedor');
    } else {
      setWinner(data);
    }
  };

  const showResults = duel => {
    setCurrentChallenge(duel);
    getWinner(duel?.id);
    setOpenPlayerModal(true);
    setCurrentStep(3);
  };

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([getTaxonomies(), getDuels(), getSentRequests(), getReceivedRequests()]).finally(() => {
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    getDuels();
  }, [filters.values]);

  useEffect(() => {
    if (!openPlayerModal) {
      getDuels();
    }
  }, [openPlayerModal]);

  const updateDuelsAndRequests = async () => {
    await Promise.all([getDuels(), getReceivedRequests()]);
  };

  const breadcrumbs = {
    title: 'Aprendizado Dinâmico',
    nav: [
      {
        route: uri,
        name: 'Duelos',
        isActive: true
      }
    ]
  };

  const filteredDuels = duels.filter(duel => filters.values.topic === 'blank' || duel?.['parent-taxonomy']?.id === Number(filters.values.topic));

  const createDuel = async newDuel => {
    const { error } = await client.mutate('duels', { invited_id: newDuel['invited-id'], parent_taxonomy_id: Number(newDuel.topic) });
    if (error) {
      if (error.message === 'already has an active duel with this user') {
        toast.error(t('toast.errorActiveDuel'));
      } else {
        toast.error(t('toast.errorCreateDuel'));
      }
    } else {
      getSentRequests();
      setNewDuelModal(false);
      toast.success(t('toast.successCreatedDuel'));
    }
  };

  const schema = Yup.object({
    topic: Yup.string().required('Campo obrigatório'),
    'invited-id': Yup.string().required('Campo obrigatório')
  });

  const duelForm = useFormik({
    initialValues: {
      topic: '',
      'invited-id': null
    },
    validationSchema: schema,
    onSubmit: values => {
      createDuel(values);
    }
  });

  const handleDialogClose = () => {
    setNewDuelModal(false);
    setSelectedOpponent({});
    duelForm.resetForm();
  };

  const statusRadios = [
    { id: 'ongoing', value: 'ongoing', label: 'Em andamento' },
    { id: 'expired', value: 'expired', label: 'Expirados' },
    { id: 'completed', value: 'completed', label: 'Finalizados' }
  ];

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <TiaBanner
        className="mb-6 mt-20"
        image={tiaDuelos}
        text="Bora duelar? 🎮 Desafie os colegas, teste o que você aprendeu e mostre quem manda no ranking. Boa sorte, guerreiro(a)! ⚔️"
      />

      <Filter variant="plain">
        <FilterContent>
          <FilterSelectNative
            blankLabel={t('filter.blankLabelAll2')}
            label={t('filter.selectTopic')}
            value={filters.values.topic}
            onChange={e => filters.setFieldValue('topic', e.target.value)}
            options={taxonomies}
          />
          <FilterRadioGroup
            value={filters.values.status}
            onValueChange={value => filters.setFieldValue('status', value)}
            radios={statusRadios}
          />
        </FilterContent>

        <FilterActions>
          <Dialog
            open={newDuelModal}
            onOpenChange={newOpenState => {
              setNewDuelModal(newOpenState);
              if (!newOpenState) {
                handleDialogClose();
              }
            }}
          >
            <DialogTrigger asChild>
              <button
                type="button"
                className="block shrink-0 rounded-full px-5 py-2.5 text-sm font-extrabold text-white transition-transform duration-100 active:translate-y-px"
                style={{ background: 'var(--gradient-btn-continue)', boxShadow: '0 3px 0 var(--color-mission-dark)' }}
              >
                Novo duelo
              </button>
            </DialogTrigger>

            <DuelsNew
              open={newDuelModal}
              taxonomies={taxonomies}
              selectedOpponent={selectedOpponent}
              setSelectedOpponent={setSelectedOpponent}
              duelForm={duelForm}
            />
          </Dialog>
        </FilterActions>
      </Filter>

      {loading && <Loader />}

      {!loading && (
        <Page>
          <PageContent>
            {filteredDuels.length > 0 && (
              <CardWrapper>
                {filteredDuels.map(duel => (
                  <DuelsPlayfulCard
                    key={duel.id}
                    handleStart={handleStart}
                    showResults={showResults}
                    duel={duel}
                    status={filters?.values?.status}
                  />
                ))}
              </CardWrapper>
            )}

            {filteredDuels.length === 0 && (
              <EmptyState
                type="select"
                title="Crie um duelo"
                text="Inicie uma novo duelo e convide um oponente para participar"
                variant="plain"
                className="pt-16"
              />
            )}
          </PageContent>

          <PageSidebar variant="md">
            <DuelsPlayfulRequests
              type="received"
              data={receivedRequests}
              setActiveTab={setActiveReceivedTab}
              activeTab={activeReceivedTab}
              updateDuelsAndRequests={updateDuelsAndRequests}
              loadingReceived={loadingReceivedRequests}
            />
            <DuelsPlayfulRequests
              type="sent"
              data={sentRequests}
              loadingSent={loadingSentRequests}
            />
          </PageSidebar>
        </Page>
      )}

      <DuelsPlayer
        setOpenPlayerModal={setOpenPlayerModal}
        setNewDuelModal={setNewDuelModal}
        duel={duel}
        openPlayerModal={openPlayerModal}
        winner={winner}
        getWinner={getWinner}
        setWinner={setWinner}
        onCloseRefresh={updateDuelsAndRequests}
      />
    </DashboardContent>
  );
}
